import { z } from 'zod';
import { getResourceTypeService, ResourceTypeEnum, ServiceEnum } from './resources.js';

/**
 * Canonical list of actions a policy statement can allow.
 *
 * Naming: `<service>:<resource-type>:<verb>`, lowercase, hyphen-separated.
 * Every action is registered in {@link ACTION_CATALOG} with the resource type it applies to,
 * its label, whether it's sensitive, and what it requires.
 */
export enum PolicyActionEnum {
  ConsoleAccess = 'mgmt:console:access',

  AccountView = 'mgmt:account:view',
  AccountManage = 'mgmt:account:manage',

  AgentView = 'mgmt:agent:view',
  AgentManage = 'mgmt:agent:manage',

  ApplicationView = 'mgmt:application:view',
  ApplicationManage = 'mgmt:application:manage',

  AuditLogView = 'mgmt:audit-log:view',

  AuthAppView = 'mgmt:auth-app:view',
  AuthAppManage = 'mgmt:auth-app:manage',

  BrandView = 'mgmt:brand:view',
  BrandManage = 'mgmt:brand:manage',

  ChatContextView = 'mgmt:chat-context:view',
  ChatContextManage = 'mgmt:chat-context:manage',

  ConnectedAppView = 'mgmt:connected-app:view',
  ConnectedAppManage = 'mgmt:connected-app:manage',

  CrmConnectionView = 'mgmt:crm-connection:view',
  CrmConnectionManage = 'mgmt:crm-connection:manage',

  DashboardView = 'mgmt:dashboard:view',
  DashboardManage = 'mgmt:dashboard:manage',

  DataConnectionView = 'mgmt:data-connection:view',
  DataConnectionManage = 'mgmt:data-connection:manage',

  DataSourceView = 'mgmt:data-source:view',
  DataSourceManage = 'mgmt:data-source:manage',

  FilterView = 'mgmt:filter:view',
  FilterManage = 'mgmt:filter:manage',

  FormView = 'mgmt:form:view',
  FormManage = 'mgmt:form:manage',

  PolicyView = 'mgmt:policy:view',
  PolicyManage = 'mgmt:policy:manage',
  PolicyGrantAny = 'mgmt:policy:grant-any',

  PromptView = 'mgmt:prompt:view',
  PromptManage = 'mgmt:prompt:manage',

  PromptQueryLogView = 'mgmt:prompt-query-log:view',

  RoleView = 'mgmt:role:view',
  RoleManage = 'mgmt:role:manage',

  SecretView = 'mgmt:secret:view',
  SecretManage = 'mgmt:secret:manage',

  ToolView = 'mgmt:tool:view',
  ToolManage = 'mgmt:tool:manage',

  UnmetNeedView = 'mgmt:unmet-need:view',
  UnmetNeedManage = 'mgmt:unmet-need:manage',

  UserView = 'mgmt:user:view',
  UserManage = 'mgmt:user:manage',
  UserAssign = 'mgmt:user:assign',

  ChatTraceView = 'chat:trace:view',
  ChatHcpViewAll = 'chat:hcp:view-all',

  QueueDashboardView = 'ops:queue-dashboard:view',
}
export const PolicyAction = z.enum(PolicyActionEnum);
export type PolicyAction = z.infer<typeof PolicyAction>;

/** An action's place in its resource's access level, for UIs that offer None / View / Manage. */
export enum ActionTierEnum {
  View = 'view',
  Manage = 'manage',
}

export type ActionDefinition = {
  resourceType: ResourceTypeEnum;
  /** Short name for UIs, unique within the resource type, e.g. "View" or "Assign brands". */
  label: string;
  description: string;
  /**
   * Lets the holder change who can do what, or reach credentials. UIs flag it when it's
   * granted. Required, so every new action gets a deliberate answer.
   */
  sensitive: boolean;
  /** The access level this action stands for on its resource; null for any other action. */
  tier: ActionTierEnum | null;
  /**
   * Actions this one is of little use without, on top of its service's entry action (see
   * {@link SERVICE_ENTRY_ACTIONS}). Editors grant or warn about them; the evaluator doesn't
   * expand them, so a policy document always lists exactly what it grants.
   */
  requires?: PolicyActionEnum[];
};

export const ACTION_CATALOG: Record<PolicyActionEnum, ActionDefinition> = {
  [PolicyActionEnum.ConsoleAccess]: {
    resourceType: ResourceTypeEnum.Console,
    label: 'Sign in',
    description: 'Sign in to the management app',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.AccountView]: {
    resourceType: ResourceTypeEnum.Account,
    label: 'View',
    description: 'View accounts',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.AccountManage]: {
    resourceType: ResourceTypeEnum.Account,
    label: 'Manage',
    description: 'Create, update and delete accounts',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.AccountView],
  },

  [PolicyActionEnum.AgentView]: {
    resourceType: ResourceTypeEnum.Agent,
    label: 'View',
    description: 'View agents and skills',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.AgentManage]: {
    resourceType: ResourceTypeEnum.Agent,
    label: 'Manage',
    description: 'Create, update and delete agents and skills',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.AgentView],
  },

  [PolicyActionEnum.ApplicationView]: {
    resourceType: ResourceTypeEnum.Application,
    label: 'View',
    description: 'View applications',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ApplicationManage]: {
    resourceType: ResourceTypeEnum.Application,
    label: 'Manage',
    description: 'Create, update and delete applications',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ApplicationView],
  },

  [PolicyActionEnum.AuditLogView]: {
    resourceType: ResourceTypeEnum.AuditLog,
    label: 'View',
    description: 'View audit logs',
    sensitive: false,
    tier: ActionTierEnum.View,
  },

  [PolicyActionEnum.AuthAppView]: {
    resourceType: ResourceTypeEnum.AuthApp,
    label: 'View',
    description: 'View authentication apps',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.AuthAppManage]: {
    resourceType: ResourceTypeEnum.AuthApp,
    label: 'Manage',
    description: 'Create, update and delete authentication apps',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.AuthAppView],
  },

  [PolicyActionEnum.BrandView]: {
    resourceType: ResourceTypeEnum.Brand,
    label: 'View',
    description: 'View brands',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.BrandManage]: {
    resourceType: ResourceTypeEnum.Brand,
    label: 'Manage',
    description: 'Create, update and delete brands',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.BrandView],
  },

  [PolicyActionEnum.ChatContextView]: {
    resourceType: ResourceTypeEnum.ChatContext,
    label: 'View',
    description: 'View chat contexts',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ChatContextManage]: {
    resourceType: ResourceTypeEnum.ChatContext,
    label: 'Manage',
    description: 'Create, update and delete chat contexts',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ChatContextView],
  },

  [PolicyActionEnum.ConnectedAppView]: {
    resourceType: ResourceTypeEnum.ConnectedApp,
    label: 'View',
    description: 'View connected apps',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ConnectedAppManage]: {
    resourceType: ResourceTypeEnum.ConnectedApp,
    label: 'Manage',
    description: 'Create, update and delete connected apps',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ConnectedAppView],
  },

  [PolicyActionEnum.CrmConnectionView]: {
    resourceType: ResourceTypeEnum.CrmConnection,
    label: 'View',
    description: 'View CRM connections',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.CrmConnectionManage]: {
    resourceType: ResourceTypeEnum.CrmConnection,
    label: 'Manage',
    description: 'Create, update and delete CRM connections',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.CrmConnectionView],
  },

  [PolicyActionEnum.DashboardView]: {
    resourceType: ResourceTypeEnum.Dashboard,
    label: 'View',
    description: 'View dashboards and pages',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.DashboardManage]: {
    resourceType: ResourceTypeEnum.Dashboard,
    label: 'Manage',
    description: 'Create, update and delete dashboards and pages',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.DashboardView],
  },

  [PolicyActionEnum.DataConnectionView]: {
    resourceType: ResourceTypeEnum.DataConnection,
    label: 'View',
    description: 'View data connections',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.DataConnectionManage]: {
    resourceType: ResourceTypeEnum.DataConnection,
    label: 'Manage',
    description: 'Create, update and delete data connections',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.DataConnectionView],
  },

  [PolicyActionEnum.DataSourceView]: {
    resourceType: ResourceTypeEnum.DataSource,
    label: 'View',
    description: 'View data sources',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.DataSourceManage]: {
    resourceType: ResourceTypeEnum.DataSource,
    label: 'Manage',
    description: 'Create, update and delete data sources',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.DataSourceView],
  },

  [PolicyActionEnum.FilterView]: {
    resourceType: ResourceTypeEnum.Filter,
    label: 'View',
    description: 'View filters',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.FilterManage]: {
    resourceType: ResourceTypeEnum.Filter,
    label: 'Manage',
    description: 'Create, update and delete filters',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.FilterView],
  },

  [PolicyActionEnum.FormView]: {
    resourceType: ResourceTypeEnum.Form,
    label: 'View',
    description: 'View form schemas and workflows',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.FormManage]: {
    resourceType: ResourceTypeEnum.Form,
    label: 'Manage',
    description: 'Create, update and delete form schemas and workflows',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.FormView],
  },

  [PolicyActionEnum.PolicyView]: {
    resourceType: ResourceTypeEnum.Policy,
    label: 'View',
    description: 'View access policies',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.PolicyManage]: {
    resourceType: ResourceTypeEnum.Policy,
    label: 'Manage',
    description: 'Create, update and delete customer-managed access policies',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.PolicyView],
  },
  [PolicyActionEnum.PolicyGrantAny]: {
    resourceType: ResourceTypeEnum.Policy,
    label: 'Grant any action',
    description:
      'Grant any action through policies, roles and imports, including actions the granter does not hold themselves',
    sensitive: true,
    tier: null,
  },

  [PolicyActionEnum.PromptView]: {
    resourceType: ResourceTypeEnum.Prompt,
    label: 'View',
    description: 'View prompts',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.PromptManage]: {
    resourceType: ResourceTypeEnum.Prompt,
    label: 'Manage',
    description: 'Create, update and delete prompts',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.PromptView],
  },

  [PolicyActionEnum.PromptQueryLogView]: {
    resourceType: ResourceTypeEnum.PromptQueryLog,
    label: 'View',
    description: 'View the prompt query log',
    sensitive: false,
    tier: ActionTierEnum.View,
  },

  [PolicyActionEnum.RoleView]: {
    resourceType: ResourceTypeEnum.Role,
    label: 'View',
    description: 'View roles and the policies attached to them',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.RoleManage]: {
    resourceType: ResourceTypeEnum.Role,
    label: 'Manage',
    description: 'Create, update and delete customer-managed roles',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.RoleView],
  },

  [PolicyActionEnum.SecretView]: {
    resourceType: ResourceTypeEnum.Secret,
    label: 'View',
    description: 'View managed secrets',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.SecretManage]: {
    resourceType: ResourceTypeEnum.Secret,
    label: 'Manage',
    description: 'Create, update and delete managed secrets',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.SecretView],
  },

  [PolicyActionEnum.ToolView]: {
    resourceType: ResourceTypeEnum.Tool,
    label: 'View',
    description: 'View tools',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ToolManage]: {
    resourceType: ResourceTypeEnum.Tool,
    label: 'Manage',
    description: 'Create and update tools',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ToolView],
  },

  [PolicyActionEnum.UnmetNeedView]: {
    resourceType: ResourceTypeEnum.UnmetNeed,
    label: 'View',
    description: 'View unmet needs',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.UnmetNeedManage]: {
    resourceType: ResourceTypeEnum.UnmetNeed,
    label: 'Manage',
    description: 'Create, update and delete unmet needs',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.UnmetNeedView],
  },

  [PolicyActionEnum.UserView]: {
    resourceType: ResourceTypeEnum.User,
    label: 'View',
    description: 'View users',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.UserManage]: {
    resourceType: ResourceTypeEnum.User,
    label: 'Manage',
    description: 'Create and update users and their policies',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.UserView],
  },
  [PolicyActionEnum.UserAssign]: {
    resourceType: ResourceTypeEnum.User,
    label: 'Assign brands',
    description: 'Add and remove brands on users',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.UserView],
  },

  [PolicyActionEnum.ChatTraceView]: {
    resourceType: ResourceTypeEnum.ChatTrace,
    label: 'View',
    description: 'View LLM trace links in chat',
    sensitive: false,
    tier: ActionTierEnum.View,
  },

  [PolicyActionEnum.ChatHcpViewAll]: {
    resourceType: ResourceTypeEnum.Hcp,
    label: 'View all HCPs',
    description: 'View all HCPs, not only those assigned to the user',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.QueueDashboardView]: {
    resourceType: ResourceTypeEnum.QueueDashboard,
    label: 'View',
    description: 'Open the background job queue dashboard',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
};

/**
 * The action a service's other actions are unusable without, such as signing in to the
 * management app. Null when the service has no such gate.
 */
export const SERVICE_ENTRY_ACTIONS: Record<ServiceEnum, PolicyActionEnum | null> = {
  [ServiceEnum.Management]: PolicyActionEnum.ConsoleAccess,
  [ServiceEnum.Chat]: null,
  [ServiceEnum.Operations]: null,
};

/** Every action `action` needs to be useful: its own requirements and its service's entry action. */
export function getActionRequirements(action: PolicyActionEnum): PolicyActionEnum[] {
  const entry = SERVICE_ENTRY_ACTIONS[getResourceTypeService(ACTION_CATALOG[action].resourceType)];
  const requires = ACTION_CATALOG[action].requires ?? [];
  return entry !== null && entry !== action && !requires.includes(entry) ? [entry, ...requires] : requires;
}

export const ALL_ACTIONS: readonly PolicyActionEnum[] = Object.values(PolicyActionEnum);
